import React, { useEffect, useRef, useState } from 'react'
import { ThemeContextValue, tbClass, uiPrimitives, useThemedStyles } from '../theme'
import { ApiError } from './api'
import type { Credential } from './api'
import {
	CodeChannel,
	fetchIdentities,
	requestCode,
	unbindIdentity,
	verifyCode,
} from './auth'

/**
 * 「登录方式」相关 UI：绑定弹窗 + 设置页里的列表。
 *
 * ## 为什么绑定要走验证码，而不是密码
 *
 * 方案 A（2026-10-21 定）全站无密码。要证明"这个邮箱/手机号是我的"，
 * 唯一可靠的方式是**能收到发到那里的消息** —— 所以绑定与登录共用同一套
 * 验证码接口：已登录时核销 = 绑定，未登录时核销 = 登录。
 *
 * ## 为什么不做"输入已有邮箱的密码来合并账号"
 *
 * 没有密码可输了。剩下的冲突场景（该邮箱已绑到别的账号）只能如实拒绝，
 * 由人工处理 —— 猜谁是谁的账号是数据事故的源头。
 */

const stylesFactory = (t: ThemeContextValue) => ({
	...uiPrimitives(t),

	backdrop: {
		position: 'fixed' as const,
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		background: 'rgba(0,0,0,.42)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 2000,
		padding: 16,
	},
	box: {
		background: t.palette.paper2,
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.lg,
		boxShadow: t.shadows.lg,
		width: 420,
		maxWidth: '100%',
		maxHeight: '86vh',
		overflowY: 'auto' as const,
		color: t.palette.ink,
		padding: '20px 22px 18px',
	},
	title: {
		fontFamily: t.fonts.serif,
		fontSize: 17,
		fontWeight: 700,
		margin: '0 0 6px',
	},
	sub: {
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		lineHeight: 1.75,
		color: t.palette.ink3,
		margin: '0 0 16px',
	},
	/** 渠道切换：两枚并排的窄按钮，选中项填青瓷色 */
	chips: {
		display: 'flex',
		gap: 8,
		marginBottom: 14,
	},
	chip: {
		flex: 1,
		padding: '8px 0',
		font: 'inherit',
		fontSize: 13,
		fontFamily: t.fonts.serif,
		border: `1px solid ${t.palette.line2}`,
		borderRadius: t.radius.sm,
		background: t.palette.paper,
		color: t.palette.ink2,
		cursor: 'pointer',
	},
	chipOn: {
		borderColor: t.palette.celadon,
		background: t.palette.celadon2,
		color: t.palette.celadonInk,
		fontWeight: 600,
	},
	chipOff: {
		opacity: .5,
		cursor: 'not-allowed' as const,
	},
	field: {
		marginBottom: 12,
	},
	label: {
		display: 'block',
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		color: t.palette.ink2,
		marginBottom: 5,
	},
	input: {
		width: '100%',
		font: 'inherit',
		fontFamily: t.fonts.sans,
		fontSize: 14,
		padding: '9px 11px',
		border: `1px solid ${t.palette.line2}`,
		borderRadius: t.radius.sm,
		background: t.palette.paper,
		color: t.palette.ink,
		outline: 'none',
		boxSizing: 'border-box' as const,
		'&:focus': {
			borderColor: t.palette.celadon,
			boxShadow: `0 0 0 3px ${t.palette.celadon2}`,
		},
	},
	/** 验证码一行：输入框 + 发送按钮，按钮固定宽度不随文案跳动 */
	codeRow: {
		display: 'flex',
		gap: 8,
	},
	codeInput: {
		flex: 1,
		minWidth: 0,
	},
	sendBtn: {
		flex: 'none',
		width: 116,
		fontSize: 12.5,
		padding: '0 8px',
		whiteSpace: 'nowrap' as const,
	},
	tip: {
		borderRadius: t.radius.sm,
		padding: '8px 11px',
		fontSize: 12.5,
		lineHeight: 1.65,
		margin: '0 0 12px',
	},
	tipError: {
		background: t.palette.cinnabar + '1f',
		color: t.palette.cinnabar,
	},
	tipInfo: {
		background: t.palette.celadon + '1f',
		color: t.palette.celadonInk,
	},
	acts: {
		display: 'flex',
		gap: 8,
		justifyContent: 'flex-end',
		marginTop: 12,
		flexWrap: 'wrap' as const,
	},
	/**
	 * 「不再提示」独占一行（在按钮行上面）。
	 *
	 * 不在按钮行里挤：这一行现在带了「（后续可在个人设置里绑定）」这句解释，
	 * 窄屏（390px）下一行塞不下复选框 + 长句 + 两个按钮。
	 */
	skipRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 6,
		marginTop: 16,
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		lineHeight: 1.6,
		color: t.palette.ink3,
		cursor: 'pointer',
		userSelect: 'none' as const,
	},
	checkbox: {
		width: 14,
		height: 14,
		// 与第一行文字对齐：复选框在行内没有 baseline，硬顶到行首会偏高
		margin: '3px 0 0',
		cursor: 'pointer',
		accentColor: t.palette.celadon,
		flex: 'none',
	},
	/** 未勾选：与说明文字同色，只是"预告一下后果" */
	skipHint: {
		color: 'inherit',
	},
	/** 勾选后转红：这一刻它就是"你选了什么"的确认，要看得见 */
	skipHintOn: {
		color: t.palette.cinnabar,
	},

	// ---- 设置页里的「登录方式」区块 ----
	section: {
		borderTop: `1px solid ${t.palette.line}`,
		paddingTop: 18,
	},
	sectionTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 14,
		fontWeight: 600,
		margin: '0 0 4px',
	},
	sectionHint: {
		fontSize: 12,
		color: t.palette.ink3,
		lineHeight: 1.6,
		margin: '0 0 12px',
	},
	credRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		padding: '9px 0',
		borderTop: `1px dashed ${t.palette.line}`,
	},
	credName: {
		fontFamily: t.fonts.serif,
		fontSize: 13,
		color: t.palette.ink,
		flex: 'none',
		width: 62,
	},
	credValue: {
		flex: 1,
		minWidth: 0,
		fontFamily: t.fonts.mono,
		fontSize: 12.5,
		color: t.palette.ink2,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap' as const,
	},
	credEmpty: {
		color: t.palette.ink3,
		fontFamily: t.fonts.serif,
	},
	credBtn: {
		flex: 'none',
		fontSize: 12.5,
		padding: '5px 12px',
		border: `1px solid ${t.palette.line2}`,
		borderRadius: t.radius.sm,
		background: 'none',
		color: t.palette.celadonInk,
		cursor: 'pointer',
	},
	credBtnDanger: {
		color: t.palette.cinnabar,
		borderColor: t.palette.cinnabar + '66',
	},
	credBtnDisabled: {
		color: t.palette.ink3,
		cursor: 'not-allowed' as const,
	},
	msg: {
		fontSize: 12.5,
		lineHeight: 1.6,
		marginTop: 8,
	},
	msgOk: {color: t.palette.celadonInk},
	msgErr: {color: t.palette.cinnabar},
})

const useStyles = () => useThemedStyles(stylesFactory)

/**
 * 「不再提示」的记忆。
 *
 * 存在 localStorage 而不是后端：这是**这台设备上别老弹**的打扰偏好，
 * 不是账号设置项，不值得为它加一个字段加一个接口。键里不带用户 id 是有意的
 * —— 这条提示本来只对"扫码进来的新账号"说一次，同一台机器上记住了就记住。
 */
const PROMPT_MUTED_KEY = 'sparks-nmn-cred-prompt-off'

export function credentialPromptMuted(): boolean {
	try {
		return localStorage.getItem(PROMPT_MUTED_KEY) === '1'
	} catch {
		// 隐私模式 / 禁用存储：当成没勾过，最多再提示一次
		return false
	}
}

export function setCredentialPromptMuted(muted: boolean) {
	try {
		if (muted) localStorage.setItem(PROMPT_MUTED_KEY, '1')
		else localStorage.removeItem(PROMPT_MUTED_KEY)
	} catch { /* 存不了就算了，不影响这次操作 */ }
}

/** 渠道的中文名与输入提示，两处 UI 共用 */
const CHANNEL_META: Record<CodeChannel, {label: string, placeholder: string, type: string}> = {
	email: {label: '邮箱', placeholder: 'you@example.com', type: 'email'},
	sms: {label: '手机号', placeholder: '11 位手机号', type: 'tel'},
}

/** 把后端的错误码翻成用户能看懂的话 */
function explain(err: unknown, channel: CodeChannel): string {
	if (err instanceof ApiError) {
		const label = CHANNEL_META[channel].label
		switch (err.code) {
			case 'target_taken':
				return `该${label}已绑定到其他账号。一个${label}只能属于一个账号。`
			case 'already_bound':
				return `当前账号已绑定其他${label}，请先解绑再绑新的。`
			case 'target_not_registered':
				return `该${label}还没有注册，无法绑定。`
			case 'too_frequent':
				return err.message
			case 'daily_limit':
			case 'ip_limit':
				return err.message
			case 'email_not_configured':
				return '邮箱验证暂未启用，请联系管理员配置发信服务。'
			case 'sms_not_configured':
				return '手机验证暂未开放（短信通道还在审核），请先绑定邮箱。'
			case 'code_expired':
			case 'code_invalid':
			case 'code_locked':
				return err.message
			default:
				return err.message
		}
	}
	return (err as any)?.message || '操作失败，请稍后重试'
}

/**
 * 绑定第二种登录方式的弹窗。
 *
 * 由两处打开：微信扫码成功后（`need_credential` 为真）与设置页的「绑定」按钮。
 * `skippable` 控制能不能"以后再说" —— 设置页里是主动来绑的，没必要给退路；
 * 登录后的引导必须能跳过，否则等于逼用户绑手机号才能用，那是最劝退的一步。
 * 能跳过时才给「不再提示」：勾上再点「以后再说」= 以后登录不再自动弹这个框
 * （设置页里「登录方式」的入口一直都在，所以不是"再也绑不了"）。
 */
export function CredentialModal(props: {
	onClose: () => void
	onBound?: () => void
	skippable?: boolean
}) {
	const classes = useStyles()
	const [channel, setChannel] = useState<CodeChannel>('email')
	const [target, setTarget] = useState('')
	const [code, setCode] = useState('')
	const [busy, setBusy] = useState(false)
	const [sending, setSending] = useState(false)
	const [sent, setSent] = useState(false)
	const [cooldown, setCooldown] = useState(0)
	const [error, setError] = useState('')
	const [info, setInfo] = useState('')
	const [available, setAvailable] = useState<{email: boolean, sms: boolean}>({email: true, sms: false})
	const [existing, setExisting] = useState<Credential[]>([])
	/** 「不再提示」勾选态。初值取上次的记忆，用户随时可以取消勾选把它撤销 */
	const [dontAsk, setDontAsk] = useState(credentialPromptMuted)

	// 拿到「哪种渠道真的能发」，避免用户填完手机号点发送才吃 503
	useEffect(() => {
		let cancelled = false
		fetchIdentities()
			.then(d => {
				if (cancelled) return
				setAvailable(d.available)
				setExisting(d.credentials)
				// 已绑过邮箱的账号默认让他绑手机号（反之亦然）
				if (d.available.sms && d.credentials.some(c => c.kind === 'email')) {
					setChannel('sms')
				}
			})
			.catch(() => { /* 读不到就用默认值，不阻塞绑定 */ })
		return () => { cancelled = true }
	}, [])

	// 倒计时：60 秒内不允许重复发（后端也是这么卡的，这里只是省一次往返）
	useEffect(() => {
		if (cooldown <= 0) return
		const id = setTimeout(() => setCooldown(n => n - 1), 1000)
		return () => clearTimeout(id)
	}, [cooldown])

	async function send() {
		setError('')
		setInfo('')
		setSending(true)
		try {
			const {ttl} = await requestCode(channel, target)
			setSent(true)
			setCooldown(60)
			setInfo(`验证码已发送，${Math.floor(ttl / 60)} 分钟内有效。没收到的话看一下垃圾邮件。`)
		} catch (err) {
			const msg = explain(err, channel)
			setError(msg)
			if (err instanceof ApiError && err.code === 'sms_not_configured') {
				setAvailable(a => ({...a, sms: false}))
			}
		} finally {
			setSending(false)
		}
	}

	async function submit() {
		setError('')
		setBusy(true)
		try {
			await verifyCode(channel, target, code)
			props.onBound?.()
			props.onClose()
		} catch (err) {
			setError(explain(err, channel))
		} finally {
			setBusy(false)
		}
	}

	/**
	 * 「以后再说」。
	 *
	 * 勾了「不再提示」才落库（以后登录不再自动弹这个框）。指路那句话**不在这里弹**
	 * —— 框一关就跳首页，飘一句提示容易被整页切换盖过去，看不见；改成常驻在
	 * 复选框后面（勾选时标红），关框之前一定读得到。
	 * 没勾就维持原来的静默关闭；取消勾选也算一次表态，把之前记住的清掉。
	 */
	function skip() {
		setCredentialPromptMuted(dontAsk)
		props.onClose()
	}

	const meta = CHANNEL_META[channel]
	const boundKinds = new Set(existing.map(c => c.kind))
	// 渠道名与 identity.kind 不同名（sms ↔ phone）—— 别直接拿 channel 去查
	const channelKind: Credential['kind'] = channel === 'email' ? 'email' : 'phone'

	return <div className={classes.backdrop} onClick={props.onClose}>
		<div className={classes.box} role="dialog" aria-modal="true" aria-label="完善账号"
			onClick={e => e.stopPropagation()}>
			<h3 className={classes.title}>再绑定一种登录方式</h3>
			<p className={classes.sub}>
				你现在只有微信可以登录。绑一个邮箱或手机号之后，可以使用邮箱或手机号接收验证码登录。
			</p>

			<div className={classes.chips}>
				{(['email', 'sms'] as CodeChannel[]).map(ch => {
					const off = !available[ch]
					return <button key={ch} type="button"
						className={`${classes.chip} ${channel === ch ? classes.chipOn : ''} ${off ? classes.chipOff : ''}`}
						onClick={() => {
							if (off) return
							setChannel(ch)
							setError('')
							setInfo('')
							setSent(false)
							setCode('')
							setTarget('')
						}}>
						{CHANNEL_META[ch].label}{off ? '（暂未开放）' : ''}
					</button>
				})}
			</div>

			<div className={classes.field}>
				<label className={classes.label} htmlFor="cred-target">{meta.label}</label>
				<input className={classes.input} id="cred-target" type={meta.type}
					value={target} placeholder={meta.placeholder}
					autoComplete={channel === 'email' ? 'email' : 'tel'}
					onChange={e => setTarget(e.target.value)} />
			</div>

			<div className={classes.field}>
				<label className={classes.label} htmlFor="cred-code">验证码</label>
				<div className={classes.codeRow}>
					<input className={`${classes.input} ${classes.codeInput}`} id="cred-code"
						value={code} placeholder="6 位数字" inputMode="numeric" maxLength={6}
						onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
					<button type="button" className={`${tbClass(classes, 'outline')} ${classes.sendBtn}`}
						disabled={sending || cooldown > 0 || !target.trim() || !available[channel]}
						onClick={send}>
						{cooldown > 0 ? `${cooldown} 秒后重发` : (sent ? '重新发送' : '获取验证码')}
					</button>
				</div>
			</div>

			{error && <div className={`${classes.tip} ${classes.tipError}`}>{error}</div>}
			{info && !error && <div className={`${classes.tip} ${classes.tipInfo}`}>{info}</div>}

			{props.skippable && <label className={classes.skipRow}>
				<input type="checkbox" className={classes.checkbox}
					checked={dontAsk}
					onChange={e => setDontAsk(e.target.checked)} />
				<span>
					不再提示
					<span className={dontAsk ? classes.skipHintOn : classes.skipHint}>
						（后续可在个人设置里绑定）
					</span>
				</span>
			</label>}

			<div className={classes.acts}>
				{props.skippable && <button className={tbClass(classes, 'outline')} onClick={skip}>
					以后再说
				</button>}
				<button className={tbClass(classes, 'primary')}
					disabled={busy || code.length < 6 || boundKinds.has(channelKind)}
					onClick={submit}>
					{busy ? '绑定中…' : '绑定'}
				</button>
			</div>
		</div>
	</div>
}

/**
 * 设置页里的「登录方式」区块。
 *
 * 与弹窗共用同一份后端数据，但这里还要展示"已绑定"和提供解绑。
 * 解绑做**两段式**（点一次变成"确认"，再点才真解）而不是 window.confirm：
 * 后者在某些内嵌 WebView 里会被吞掉，用户点了没反应。
 */
export function CredentialSection(props: {
	/** 绑定/解绑成功后通知父组件刷新（/auth/me 的 credentials 变了） */
	onChanged?: () => void
}) {
	const classes = useStyles()
	const [list, setList] = useState<Credential[]>([])
	const [available, setAvailable] = useState<{email: boolean, sms: boolean}>({email: true, sms: false})
	const [target, setTarget] = useState<Credential['kind'] | null>(null)
	const [confirming, setConfirming] = useState<Credential['kind'] | null>(null)
	const [busy, setBusy] = useState(false)
	const [msg, setMsg] = useState<{ok: boolean, text: string} | null>(null)
	const timer = useRef<number | null>(null)

	async function load() {
		try {
			const d = await fetchIdentities()
			setList(d.credentials)
			setAvailable(d.available)
		} catch (err) {
			setMsg({ok: false, text: (err as any)?.message ?? '读取登录方式失败'})
		}
	}

	useEffect(() => {
		void load()
		return () => { if (timer.current) window.clearTimeout(timer.current) }
	}, [])

	/** 两段式确认：3 秒内不再点就自动收回，避免一直停在"确认解绑"上 */
	function armUnbind(kind: Credential['kind']) {
		setConfirming(kind)
		if (timer.current) window.clearTimeout(timer.current)
		timer.current = window.setTimeout(() => setConfirming(null), 3000)
	}

	async function doUnbind(kind: Credential['kind']) {
		setBusy(true)
		setMsg(null)
		try {
			const d = await unbindIdentity(kind)
			setList(d.credentials)
			setConfirming(null)
			setMsg({ok: true, text: '已解绑。下次登录请使用剩下的方式。'})
			props.onChanged?.()
		} catch (err) {
			setMsg({ok: false, text: explain(err, kind === 'email' ? 'email' : 'sms')})
		} finally {
			setBusy(false)
		}
	}

	const rows: {kind: Credential['kind'], label: string}[] = [
		{kind: 'wechat', label: '微信'},
		{kind: 'email', label: '邮箱'},
		{kind: 'phone', label: '手机号'},
	]

	// 已经绑过邮箱就只有"手机号"可绑，反之亦然；两种都绑了就没有可绑的了
	const canBind = !(list.some(c => c.kind === 'email') && list.some(c => c.kind === 'phone'))
	const onlyOne = list.length <= 1

	return <div className={classes.section}>
		<h4 className={classes.sectionTitle}>登录方式</h4>
		<p className={classes.sectionHint}>
			微信是注册入口，邮箱/手机号用来在换号或丢号时登回来。
			<b>至少要留一种</b>，否则账号就进不去了。本站不设密码，登录一律用验证码。
		</p>

		{rows.map(({kind, label}) => {
			const found = list.find(c => c.kind === kind)
			// 手机号未开通时仍显示这一行，但按钮置灰 —— 藏起来会让人以为"没这个功能"
			const off = kind === 'phone' && !available.sms
			return <div key={kind} className={classes.credRow}>
				<span className={classes.credName}>{label}</span>
				<span className={`${classes.credValue} ${found?.hint ? '' : classes.credEmpty}`}>
					{found ? (found.hint || '已绑定') : '未绑定'}
				</span>
				{found
					? <button className={`${classes.credBtn} ${confirming === kind ? classes.credBtnDanger : ''} ${onlyOne ? classes.credBtnDisabled : ''}`}
						disabled={busy || onlyOne}
						title={onlyOne ? '这是唯一的登录方式，不能再解绑' : ''}
						onClick={() => confirming === kind ? doUnbind(kind) : armUnbind(kind)}>
						{confirming === kind ? '确认解绑' : '解绑'}
					</button>
					: <button className={`${classes.credBtn} ${(off || !canBind) ? classes.credBtnDisabled : ''}`}
						disabled={busy || off || !canBind}
						title={off ? '短信通道还在审核，暂未开放' : (!canBind ? '已经绑定过邮箱与手机号' : '')}
						onClick={() => setTarget(kind)}>
						{off ? '暂未开放' : '绑定'}
					</button>}
			</div>
		})}

		{msg && <div className={`${classes.msg} ${msg.ok ? classes.msgOk : classes.msgErr}`}>{msg.text}</div>}

		{target && <CredentialModal
			onClose={() => setTarget(null)}
			onBound={() => { void load(); props.onChanged?.() }} />}
	</div>
}
